<script setup lang="ts">
import { computed } from 'vue';
import EmptyState from '@/components/base/EmptyState.vue';
import { XCircleIcon } from '@heroicons/vue/20/solid';
import type { MenuItem, MenuCategory } from '@/types/menu';
import { useI18n } from 'vue-i18n';
import { usePermissions } from '@/composables/usePermissions';
import { platformFeatures } from '@/utils/platform';
import PageHeader from '@/components/layout/PageHeader.vue';
import BaseButton from '@/components/common/BaseButton.vue';
import ViewModeToggle from '@/components/common/ViewModeToggle.vue';
import MenuListFilters from './MenuListFilters.vue';
import MenuListMobileCards from './MenuListMobileCards.vue';
import MenuListDesktopTable from './MenuListDesktopTable.vue';
import MenuItemCard from './MenuItemCard.vue';
import LoadingSpinner from '@/components/base/LoadingSpinner.vue';

interface Props {
  menuItems: MenuItem[];
  loading: boolean;
  error: string | null;
  categories: MenuCategory[];
  selectedCategoryId: number | null;
  searchQuery: string;
  viewMode: 'list' | 'card';
}

interface Emits {
  (e: 'add-item'): void;
  (e: 'edit-item', item: MenuItem): void;
  (e: 'delete-item', item: MenuItem): void;
  (e: 'toggle-availability', item: MenuItem): void;
  (e: 'filter-category', categoryId: number | null): void;
  (e: 'search-change', query: string): void;
  (e: 'update:viewMode', value: 'list' | 'card'): void;
}

const props = defineProps<Props>();
const emit = defineEmits<Emits>();
const { t } = useI18n();
const { canEditMenu } = usePermissions();

const canManageItems = computed(() => platformFeatures.cloudOnly.menuManagement && canEditMenu.value);

function handleEditItem(item: MenuItem) {
  emit('edit-item', item);
}

function handleDeleteItem(item: MenuItem) {
  emit('delete-item', item);
}

function handleToggleAvailability(item: MenuItem) {
  emit('toggle-availability', item);
}
</script>

<template>
  <div>
    <PageHeader
      :title="t('app.views.menu.list.title')"
      :subtitle="t('app.views.menu.list.subtitle')"
    >
      <template #actions>
        <div class="flex items-center gap-3">
          <!-- View Mode Toggle -->
          <ViewModeToggle 
            :view-mode="props.viewMode" 
            @update:view-mode="$emit('update:viewMode', $event)" 
          />
          
          <!-- Add button -->
          <BaseButton
            v-if="canManageItems"
            @click="emit('add-item')"
          >
            {{ t('app.views.menu.list.add') }}
          </BaseButton>
        </div>
      </template>
    </PageHeader>

    <MenuListFilters
      :categories="props.categories"
      :selected-category-id="props.selectedCategoryId"
      :search-query="props.searchQuery"
      @filter-category="emit('filter-category', $event)"
      @search-change="emit('search-change', $event)"
    />

    <div class="mt-4 sm:mt-6 flow-root">
      <div class="bg-white dark:bg-gray-950 rounded-lg shadow overflow-hidden">
        <LoadingSpinner
          v-if="loading"
          size="md"
          color="primary"
          :show-text="false"
        />
        <div
          v-else-if="error"
          class="rounded-md bg-red-50 p-4 mb-4 dark:bg-red-950"
        >
          <div class="flex">
            <div class="flex-shrink-0">
              <XCircleIcon
                class="h-5 w-5 text-red-400"
                aria-hidden="true"
              />
            </div>
            <div class="ml-3">
              <h3 class="text-sm font-medium text-red-800">
                {{ t('app.views.menu.list.error_title') }}
              </h3>
              <div class="mt-2 text-sm text-red-700">
                <p>{{ error }}</p>
              </div>
            </div>
          </div>
        </div>
        <div
          v-else
          class="overflow-x-auto"
        >
          <div class="inline-block min-w-full py-2 align-middle">
            <!-- Mobile view - always cards -->
            <MenuListMobileCards
              :menu-items="props.menuItems"
              :search-query="props.searchQuery"
              :can-manage-items="canManageItems"
              @edit-item="handleEditItem"
              @delete-item="handleDeleteItem"
              @toggle-availability="handleToggleAvailability"
            />

            <!-- Desktop view - based on viewMode -->
            <div class="hidden md:block">
              <!-- Card View -->
              <div v-if="props.viewMode === 'card'">
                <EmptyState
                  v-if="props.menuItems.length === 0"
                  icon="search"
                  :title="props.searchQuery.trim() ? (t('app.common.no_results') || 'No se encontraron productos') : t('app.views.menu.list.empty')"
                  variant="minimal"
                  size="md"
                />
                <div
                  v-else
                  class="columns-1 md:columns-2 lg:columns-3 column-gap-2 sm:column-gap-3"
                >
                  <MenuItemCard
                    v-for="item in props.menuItems"
                    :key="item.id"
                    :item="item"
                    :can-manage="canManageItems"
                    class="break-inside-avoid mb-3 sm:mb-4"
                    @edit="handleEditItem"
                    @delete="handleDeleteItem"
                    @toggle-availability="handleToggleAvailability"
                  />
                </div>
              </div>

              <!-- List View -->
              <MenuListDesktopTable
                v-else
                :menu-items="props.menuItems"
                :search-query="props.searchQuery"
                :can-manage-items="canManageItems"
                @edit-item="handleEditItem"
                @delete-item="handleDeleteItem"
                @toggle-availability="handleToggleAvailability"
              />
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>
